import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

type EgoState = 'Parent' | 'Adult' | 'Child';

export interface EgoAnalyzeResponse {
  distribution: Record<EgoState, number>;
  scores: Record<EgoState, number>;
  dominant: EgoState | 'Balance';
  dominantLocalized: string;
  keywords: Record<EgoState, string[]>;
  meta: {
    langDetected:
      | 'ru'
      | 'en'
      | 'gb'
      | 'de'
      | 'es'
      | 'pt'
      | 'tr'
      | 'ar'
      | 'zh'
      | 'ja'
      | 'id'
      | 'kr';
    textLength: number;
    tokenCount: number;
    version: string;
    analyzedAt: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class EgoAnalyzeService {
  constructor(private readonly http: HttpClient) {}

  private localizeDominant(
    dominant: EgoAnalyzeResponse['dominant'],
    lang: EgoAnalyzeResponse['meta']['langDetected'],
  ): string {
    const labels: Record<
      EgoAnalyzeResponse['meta']['langDetected'],
      Record<EgoAnalyzeResponse['dominant'], string>
    > = {
      ru: {
        Parent: 'Родитель',
        Adult: 'Взрослый',
        Child: 'Ребенок',
        Balance: 'Баланс',
      },
      en: { Parent: 'Parent', Adult: 'Adult', Child: 'Child', Balance: 'Balance' },
      gb: { Parent: 'Parent', Adult: 'Adult', Child: 'Child', Balance: 'Balance' },
      de: {
        Parent: 'Eltern-Ich',
        Adult: 'Erwachsenen-Ich',
        Child: 'Kind-Ich',
        Balance: 'Balance',
      },
      es: {
        Parent: 'Padre',
        Adult: 'Adulto',
        Child: 'Niño',
        Balance: 'Equilibrio',
      },
      pt: {
        Parent: 'Pai',
        Adult: 'Adulto',
        Child: 'Criança',
        Balance: 'Equilibrio',
      },
      tr: {
        Parent: 'Ebeveyn',
        Adult: 'Yetiskin',
        Child: 'Cocuk',
        Balance: 'Denge',
      },
      ar: {
        Parent: 'الوالد',
        Adult: 'البالغ',
        Child: 'الطفل',
        Balance: 'توازن',
      },
      zh: { Parent: '父母', Adult: '成人', Child: '儿童', Balance: '平衡' },
      ja: { Parent: '親', Adult: '大人', Child: '子ども', Balance: 'バランス' },
      id: {
        Parent: 'Orang Tua',
        Adult: 'Dewasa',
        Child: 'Anak',
        Balance: 'Seimbang',
      },
      kr: { Parent: '부모', Adult: '성인', Child: '아이', Balance: '균형' },
    };

    return labels[lang][dominant];
  }

  private normalizeResponse(payload: unknown): EgoAnalyzeResponse {
    const source =
      payload &&
      typeof payload === 'object' &&
      'data' in payload &&
      (payload as { data?: unknown }).data
        ? (payload as { data: unknown }).data
        : payload;

    if (!source || typeof source !== 'object') {
      throw new Error('Invalid ego analyze response: not an object');
    }

    const typed = source as Partial<EgoAnalyzeResponse>;
    const fallbackDist = { Parent: 33, Adult: 34, Child: 33 };
    const fallbackScores = { Parent: 0, Adult: 0, Child: 0 };
    const langDetected = typed.meta?.langDetected ?? 'en';
    const dominant = typed.dominant ?? 'Balance';

    return {
      distribution: {
        Parent: typed.distribution?.Parent ?? fallbackDist.Parent,
        Adult: typed.distribution?.Adult ?? fallbackDist.Adult,
        Child: typed.distribution?.Child ?? fallbackDist.Child,
      },
      scores: {
        Parent: typed.scores?.Parent ?? fallbackScores.Parent,
        Adult: typed.scores?.Adult ?? fallbackScores.Adult,
        Child: typed.scores?.Child ?? fallbackScores.Child,
      },
      dominant,
      dominantLocalized:
        typed.dominantLocalized ??
        this.localizeDominant(dominant, langDetected),
      keywords: {
        Parent: typed.keywords?.Parent ?? [],
        Adult: typed.keywords?.Adult ?? [],
        Child: typed.keywords?.Child ?? [],
      },
      meta: {
        langDetected,
        textLength: typed.meta?.textLength ?? 0,
        tokenCount: typed.meta?.tokenCount ?? 0,
        version: typed.meta?.version ?? 'unknown',
        analyzedAt: typed.meta?.analyzedAt ?? new Date().toISOString(),
      },
    };
  }

  async analyzeText(
    text: string,
    lang:
      | 'ru'
      | 'en'
      | 'gb'
      | 'de'
      | 'es'
      | 'pt'
      | 'tr'
      | 'ar'
      | 'zh'
      | 'ja'
      | 'id'
      | 'kr'
      | 'auto' = 'auto',
  ) {
    const baseUrl = (environment.apiUrl || '').replace(/\/$/, '');
    const endpoint = '/api/ego/analyze';
    const url = baseUrl ? `${baseUrl}${endpoint}` : endpoint;

    const response = await firstValueFrom(
      this.http.post<unknown>(url, {
        text,
        lang,
      }),
    );
    return this.normalizeResponse(response);
  }
}
